import { MapPin, Check, X, Trophy } from 'lucide-react'
import { useNav } from '@/shared/hooks/use-nav'
import { Card } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { SeatStrip } from '@/shared/components/seat-strip'
import { MatchTypeBadge } from '@/shared/ui/match-type-badge'
import { formatNivelRange } from '@/shared/levels'
import { freeSlots, getProfile, type MockMatch } from '@/shared/mock/data'
import { useAuthStore } from '@/core/auth/auth-store'

// The uid fields `MatchView` carries. Spelled out here rather than imported,
// because `shared/` may not import from `features/` (lint-enforced) and
// MatchView lives in features/partidos. Optional: the AUTH_BYPASS mock track
// has no uids, so the card falls back to names there.
type MatchCardMatch = MockMatch & { playerUids?: string[]; organizerUid?: string }

// Match summary card — the core unit of the Partidos feed + Mis partidos.
export function MatchCard({ match }: { match: MatchCardMatch }) {
  const navigate = useNav()
  const profile = getProfile()
  const myUid = useAuthStore((s) => s.user?.uid)
  const free = freeSlots(match)
  const dim = match.status === 'past' || match.status === 'cancelled'
  // Membership by UID when the real data carries it — `MatchView` even
  // documents "UID truth for membership decisions (names are display-only)"
  // and this card was the one place ignoring it. Two players called "Carlos
  // García" in the same city made the feed show a stranger's match as
  // "Organizas", and padel clubs are exactly where common names collide.
  // Names remain the fallback for the mock track, which has no uids.
  const organizes =
    match.organizerUid && myUid ? match.organizerUid === myUid : match.organizer === profile.name
  const joined =
    !organizes &&
    !dim &&
    (match.playerUids && myUid
      ? match.playerUids.includes(myUid)
      : match.players.some((p) => p.name === profile.name))
  // Past/cancelled state via chips + muted accents — NOT whole-card opacity,
  // which dragged AA-passing text below 4.5:1 (axe serious, cert-walk-10).
  return (
    <Card onClick={() => navigate(`/partido/${match.id}`)}>
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-[length:var(--fs-md)] truncate">{match.place}</p>
            <p className="flex items-center gap-1 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
              <MapPin size={13} /> {match.city}
              {match.distanceKm != null && (
                <span className="text-[color:var(--fg-faint)]">· {match.distanceKm} km</span>
              )}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className={`font-bold leading-none ${dim ? 'text-[color:var(--fg-muted)]' : 'text-[color:var(--fg)]'}`}>{match.timeLabel}</p>
            <p className="text-[length:var(--fs-xs)] text-[color:var(--fg-muted)] mt-0.5">{match.dayLabel}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {match.status === 'cancelled' && (
            <Chip className="text-[color:var(--bad)] border-[var(--bad)]">
              <X size={13} /> Cancelado
            </Chip>
          )}
          {organizes && <Chip className="text-[color:var(--accent)] border-[var(--accent)]">Organizas</Chip>}
          {joined && (
            <Chip className="text-[color:var(--ok)] border-[var(--ok)]">
              <Check size={13} /> Apuntado
            </Chip>
          )}
          <Chip>{formatNivelRange(match.levelMin, match.levelMax)}</Chip>
          {/* `free > 0` matches match-detail-page and match-preview-page, which
              both had this guard. Without it a full match advertised "Buscan
              revés" directly beside its own "Completo · 4/4" — the card
              contradicting itself on the surface both personas see most. */}
          {free > 0 && match.position !== 'Indiferente' && (
            <Chip>Buscan {match.position.toLowerCase()}</Chip>
          )}
          {match.cost != null && <Chip>{match.cost.toFixed(2).replace('.', ',')} €/jug</Chip>}
          {match.genderCategory != null && match.genderCategory !== 'open' && (
            <Chip>
              {match.genderCategory === 'women'
                ? 'Femenino'
                : match.genderCategory === 'men'
                  ? 'Masculino'
                  : 'Mixto'}
            </Chip>
          )}
          <MatchTypeBadge type={match.matchType} />
          <Chip className={match.courtReserved ? 'text-[color:var(--ok)] border-[var(--ok)]' : ''}>
            {match.courtReserved ? <Check size={13} /> : <X size={13} />}
            {match.courtReserved ? 'Con pista' : 'Sin pista'}
          </Chip>
        </div>

        {match.note && <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)] line-clamp-2">{match.note}</p>}

        <div className="flex items-center justify-between">
          {/* One box per seat, always — see SeatStrip. The old stack showed
              the PLAYERS only, so a match with one player and two off-app
              companions rendered a single avatar beside "3/4" and the reader
              had to reconcile the two. */}
          <SeatStrip
            players={match.players}
            companions={match.offAppCompanions ?? 0}
            capacity={match.capacity}
          />
          <span className="text-[length:var(--fs-sm)] font-semibold">
            {match.status === 'cancelled' ? (
              <span className="text-[color:var(--bad)]">Cancelado</span>
            ) : free > 0 ? (
              <span className="text-[color:var(--accent)]">Faltan {free}</span>
            ) : (
              <span className="text-[color:var(--fg-muted)]">Completo</span>
            )}
            {/* The "3/4" that used to sit here is gone. The SeatStrip to the
                left already draws one box per seat, which is the whole reason
                it exists: answer "is there room for me" by COUNTING, not by
                reading arithmetic. Printing the same fact a third time — boxes,
                "Faltan 1", and "3/4" — made the strip decorative and the card
                busier. "Faltan 1" survives because it is not the same fact: it
                is urgency, and it is what a scanner reads first. */}
          </span>
        </div>

        {match.status === 'past' && !match.result && (
          <p className="flex items-center gap-1.5 pt-2 -mb-1 border-t border-[var(--line)] text-[length:var(--fs-sm)] font-semibold text-[color:var(--accent)]">
            <Trophy size={14} /> Registrar resultado
          </p>
        )}
      </div>
    </Card>
  )
}
